<template>
  <section class="section-container" id="faq">
    <div class="section-container-header-box fade-up-animation">
      <p class="list-text">Frequently Asked Questions</p>
    </div>
    <h2 class="section-container-heading">
      <p
        v-for="(line, index) in lines"
        :key="line.text"
        class="extra-large-text-list fade-up-animation"
        :class="{ 'special-text': line.special }">
        {{ line.text }}
      </p>
    </h2>

    <div class="faq-box-questions">
      <div
        v-for="question in questions.slice(0, props.limitQuestions || questions.length)"
        :key="question.id"
        class="faq-box-question fade-up-animation"
        @click="
          openedQuestions.includes(question.id)
            ? openedQuestions.splice(openedQuestions.indexOf(question.id), 1)
            : openedQuestions.push(question.id)
        ">
        <div class="faq-box-question-header">
          <h3 class="heading-5">{{ question.question }}</h3>
          <div class="arrow-box">
            <div
              class="arrow"
              :class="{
                'top-arrow': openedQuestions.includes(question.id),
                'bottom-arrow': !openedQuestions.includes(question.id),
              }"></div>
          </div>
        </div>
        <ClientOnly>
          <p
            class="faq-box-question-answer"
            v-html="question.answer"
            :class="{
              'faq-box-question-answer-open': openedQuestions.includes(question.id),
            }"></p>
        </ClientOnly>
      </div>
    </div>

    <nuxt-link
      to="/faq"
      aria-label="Navigate to FAQ page"
      class="arrow-button arrow-button-bigger fade-up-animation"
      v-if="props.limitQuestions">
      <p class="heading-4-bold">See all FAQs</p>
      <div class="arrow-button-icon" v-html="[icons.rightArrowIcon, icons.rightArrowIcon]"></div>
    </nuxt-link>
  </section>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import icons from '~/icons';

const props = defineProps<{
  limitQuestions?: number;
}>();

const openedQuestions = ref<number[]>([]);

const lines = [
  { text: 'Your questions,', special: false },
  { text: 'answered.', special: true },
];

const questions = [
  // 1. Getting Started
  {
    id: 1,
    question: 'How do I get started?',
    answer: `Book a free call and let's talk about your goals. We'll guide you through every step.<br/>
      <a href="https://calendly.com/vortec-io" target="_blank" rel="noopener noreferrer" aria-label="Book a free call" class="arrow-button ">
        <p class="list-text-bold">Book a call</p>
        <div class="arrow-button-icon">${[icons.rightArrowIcon, icons.rightArrowIcon]}</div>
      </a>`,
  },
  {
    id: 2,
    question: 'What should I prepare before we begin?',
    answer: `<ul>
      <li>Logo and brand assets</li>
      <li>Content (text, images)</li>
      <li>Design inspiration</li>
    </ul>
    <br/>We’ll handle the roadmap, wireframes, and all technical setup.`,
  },

  // 2. Pricing & Payment
  {
    id: 3,
    question: 'How is pricing calculated?',
    answer: `We price based on project scope, design, and features. You get a clear proposal after our first call—no surprises.`,
  },
  {
    id: 4,
    question: 'What are your payment terms?',
    answer: `<ul>
      <li>50% upfront</li>
      <li>25% at halfway point</li>
      <li>25% on delivery</li>
    </ul>`,
  },
  {
    id: 5,
    question: 'Are there any extra costs?',
    answer: `No hidden fees. Hosting and third-party tools are billed separately and always listed in your proposal.`,
  },

  // 3. Process & Timeline
  {
    id: 6,
    question: 'How long does a project take?',
    answer: `Most projects are completed in 2–4 months, depending on complexity.`,
  },
  {
    id: 7,
    question: 'How do you estimate timelines?',
    answer: `We break work into 8-hour tasks and compare with similar projects. You’ll get regular updates and honest forecasts.`,
  },
  {
    id: 8,
    question: 'What’s your process for working together?',
    answer: `<ul>
      <li>Discovery call</li>
      <li>Proposal in 24–48h</li>
      <li>Weekly updates during development</li>
    </ul>
    We use agile methods and async communication for a smooth experience.`,
  },
  {
    id: 9,
    question: 'How will I stay updated?',
    answer: `You'll receive weekly reports and a Visual Changelog showing exactly what's been done.`,
  },

  // 4. Collaboration & Responsibilities
  {
    id: 10,
    question: 'How do we collaborate?',
    answer: `We deliver quality and transparency. You join weekly reviews and share feedback—success is a team effort.`,
  },
  {
    id: 11,
    question: 'Can you sign an NDA?',
    answer: `Absolutely. We’re happy to sign NDAs or confidentiality agreements to protect your ideas.`,
  },

  // 5. Delivery & Support
  {
    id: 12,
    question: 'What support is available after launch?',
    answer: `We offer ongoing support for bug fixes, updates, and improvements—ad-hoc or as a monthly package.`,
  },

  // 6. Technical & Features
  {
    id: 13,
    question: 'Will my site work on mobile?',
    answer: `Yes. Every site we build is fully responsive and optimized for all devices.`,
  },
  {
    id: 14,
    question: 'Can you connect third-party tools?',
    answer: `Of course. We integrate CRMs, analytics, booking systems, and more—just let us know what you need.`,
  },
];

useFadeUpObserver();

function formatToJSONLD() {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((question) => ({
      '@type': 'Question',
      name: question.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: formatAnswer(question.answer),
      },
    })),
  });
}

function formatAnswer(answer: string) {
  let text = answer.replace(/\$\{.*?\}/g, '');
  text = text.replace(/<svg[\s\S]*?<\/svg>/g, '');
  text = text.replace(/<\/?(?!a\b)[^>]*>/g, '');
  text = text.replace(/<\/a>,/g, '</a>');
  text = text.replace(/<li>(.*?)<\/li>/g, '• $1\n');
  text = text.replace(/<\/?ul[^>]*>/g, '\n');
  return text.trim();
}

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: formatToJSONLD(),
    },
  ],
});

defineExpose({
  questions: questions.slice(0, props.limitQuestions || questions.length),
  allQuestions: questions,
});
</script>
